import {
  CashFlowIncome,
  CashFlowIncomeConcept,
  CashFlowIncomeWeek,
  CashFlowWeekStatus,
} from "../../../domain/models/accounting/cash-flow.model";

type UnknownRecord = Record<string, unknown>;

const WEEK_STATUSES: readonly string[] = ["REAL", "PROJECTED"];

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const isText = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

function toWeek(value: unknown): CashFlowIncomeWeek | null {
  if (
    !isRecord(value)
    || !isFiniteNumber(value["idPeriod"])
    || !isFiniteNumber(value["number"])
    || !isText(value["startDate"])
    || !isText(value["endDate"])
    || !WEEK_STATUSES.includes(String(value["status"]))
    || !isFiniteNumber(value["income"])
    || !isFiniteNumber(value["realIncome"])
    || !isFiniteNumber(value["previousYearIncome"])
  ) {
    return null;
  }
  return {
    idPeriod: value["idPeriod"],
    number: value["number"],
    startDate: value["startDate"],
    endDate: value["endDate"],
    status: value["status"] as CashFlowWeekStatus,
    income: value["income"],
    realIncome: value["realIncome"],
    previousYearIncome: value["previousYearIncome"],
  };
}

function toConcept(value: unknown): CashFlowIncomeConcept | null {
  if (!isRecord(value) || !isText(value["code"]) || !isText(value["name"]) || !isFiniteNumber(value["amount"])) {
    return null;
  }
  return { code: value["code"], name: value["name"], amount: value["amount"] };
}

function listOf<T>(value: unknown, parse: (item: unknown) => T | null): T[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const items = value.map(parse);
  return items.some((item) => item === null) ? null : (items as T[]);
}

export function toCashFlowIncome(value: unknown): CashFlowIncome | null {
  if (
    !isRecord(value)
    || !isText(value["yearMonth"])
    || !isText(value["baseMonth"])
    || !isFiniteNumber(value["realIncome"])
    || !isFiniteNumber(value["projectedIncome"])
  ) {
    return null;
  }
  const weeks = listOf(value["weeks"], toWeek);
  const concepts = listOf(value["concepts"], toConcept);
  if (!weeks || !concepts) {
    return null;
  }
  return {
    yearMonth: value["yearMonth"],
    baseMonth: value["baseMonth"],
    weeks,
    concepts,
    realIncome: value["realIncome"],
    projectedIncome: value["projectedIncome"],
  };
}
